import { useState } from 'react';

import styles from './Avatar.module.css';

type AvatarSize = 'sm' | 'md' | 'lg';

/**
 * Изображение Karl в `public/editorial/`: отдаётся с того же origin, поэтому
 * production-CSP (`img-src 'self' data:`) выполняется без исключений.
 */
const KARL_AVATAR_SRC = '/editorial/karl-avatar.webp';

/** Буква на случай, если изображение Karl не загрузилось. */
const KARL_INITIAL = 'K';

interface AvatarBaseProps {
  size?: AvatarSize | undefined;
  className?: string | undefined;
}

/** Буквенная плашка: первая буква имени владельца в круге акцента. */
interface InitialAvatarProps extends AvatarBaseProps {
  variant?: 'initial' | undefined;
  /** Видимая подпись владельца аватара живёт рядом; сам аватар декоративен. */
  initial: string;
  /**
   * Синяя точка присутствия. Только индикатор: текстовое состояние
   * («online») обязано стоять рядом.
   */
  presence?: boolean | undefined;
}

/**
 * Karl — изображение в круге с синим свечением. Точки присутствия у Karl нет:
 * `presence` для этого варианта не компилируется.
 */
interface KarlAvatarProps extends AvatarBaseProps {
  variant: 'karl';
  initial?: never;
  presence?: never;
}

type AvatarProps = InitialAvatarProps | KarlAvatarProps;

/**
 * Аватар DESIGN_SYSTEM §4.2: круг `--radius-avatar`, размеры 32 / 40 / 56 px.
 *
 * Два варианта:
 *  - буквенная плашка (`initial`) — по умолчанию;
 *  - Karl (`variant="karl"`) — изображение `/editorial/karl-avatar.webp`
 *    со свечением `--glow-karl-ring`, без точки присутствия.
 *
 * **Изображение Karl — сгенерированный образ AI-помощника, а не фотография
 * сотрудника или реального человека.** Поэтому рядом с ним всегда стоит
 * текстовая подпись «Karl · KI-Assistent» («AI-ассистент», «AI assistant»):
 * без неё вариант не используется (MVP_PRD §26, DESIGN_SYSTEM §5.4). Сам
 * аватар декоративен — `alt=""` и `aria-hidden`: имя и маркировку AI
 * скринридер читает из этой подписи, а не из картинки.
 */
export function Avatar(props: AvatarProps) {
  const { size = 'md', className } = props;

  if (props.variant === 'karl') {
    return <KarlAvatar size={size} className={className} />;
  }

  return (
    <span
      className={[styles.avatar, styles[size], className].filter(Boolean).join(' ')}
      aria-hidden="true"
    >
      <span className={styles.letter}>{props.initial.charAt(0).toUpperCase()}</span>
      {props.presence === true ? <span className={styles.presence} /> : null}
    </span>
  );
}

function KarlAvatar({ size, className }: { size: AvatarSize; className: string | undefined }) {
  // Не загрузилось изображение — буква на том же круге, а не «сломанная» картинка.
  const [failed, setFailed] = useState(false);

  return (
    <span
      className={[styles.avatar, styles.karl, styles[size], className].filter(Boolean).join(' ')}
      aria-hidden="true"
    >
      {failed ? (
        <span className={styles.letter}>{KARL_INITIAL}</span>
      ) : (
        <img
          className={styles.photo}
          src={KARL_AVATAR_SRC}
          alt=""
          decoding="async"
          draggable={false}
          onError={() => {
            setFailed(true);
          }}
        />
      )}
    </span>
  );
}
